import type { ClarificationAnswers } from "@prelude-ai/design-system"
import type { ClarifyAnswer } from "@prelude-ai/prelude-agentic-framework"
import { useCallback, useReducer, useRef, useState } from "react"
import type { ApiConversationState } from "../../utils/conversation-store.ts"
import { API_BASE_URL } from "../config.ts"
import type { StagedAttachment } from "../types.ts"
import { deriveDevToolsStateFromTranscript, parseSSEBuffer, transcriptToChatMessages } from "../utils/chat-state.ts"
import { toast } from "../utils/toast.tsx"
import { uploadFiles } from "../utils/upload-files.ts"
import { type ChatAction, chatReducer, createMessage, initialState } from "./chat-reducer.ts"

export { initialState } from "./chat-reducer"

// ============================================================================
// Hook
// ============================================================================

export type UseChatSessionOptions = {
  agentName: string
  backgroundMode?: boolean
  conversationId?: string
  initialFlowContext?: Record<string, unknown>
  initialMessages?: ApiConversationState["transcript"]
  onAuthError?: () => void
}

export function useChatSession({
  agentName,
  backgroundMode = false,
  conversationId,
  initialFlowContext,
  initialMessages,
  onAuthError
}: UseChatSessionOptions) {
  const [state, dispatch] = useReducer(
    chatReducer,
    initialMessages
      ? {
          ...initialState,
          messages: transcriptToChatMessages(initialMessages),
          ...deriveDevToolsStateFromTranscript(initialMessages)
        }
      : initialState
  )
  const eventSourceRef = useRef<EventSource | null>(null)
  const flowContextSentRef = useRef(false)
  const [attachments, setAttachments] = useState<StagedAttachment[]>([])

  const uploadFile = useCallback(
    async (files: File[]) => {
      await uploadFiles({
        files,
        agentName,
        conversationId,
        setAttachments,
        onAuthError
      })
    },
    [agentName, conversationId, onAuthError]
  )

  const isUploading = attachments.some((a) => a.status === "uploading")

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const streamRequest = useCallback(
    async (body: Record<string, unknown>): Promise<boolean> => {
      const endpoint = backgroundMode ? "run-background" : "chat"
      try {
        const response = await fetch(`${API_BASE_URL}/api/agents/${agentName}/${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ conversationId, ...body })
        })

        if (response.status === 401) {
          onAuthError?.()
          return false
        }

        if (!response.ok) {
          const desc = `POST /api/agents/${agentName}/${endpoint} returned ${response.status} (conversation: ${conversationId ?? "none"})`
          toast.error("Failed to run agent", { description: desc })
          dispatch({ type: "STREAM_ERROR", error: desc })
          return false
        }

        flowContextSentRef.current = true

        const reader = response.body?.getReader()
        if (!reader) {
          const desc = `POST /api/agents/${agentName}/${endpoint} returned no response body (conversation: ${conversationId ?? "none"})`
          toast.error("Failed to run agent", { description: desc })
          dispatch({ type: "STREAM_ERROR", error: desc })
          return false
        }

        const decoder = new TextDecoder()
        let buffer = ""

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const { events, remaining } = parseSSEBuffer(buffer)
          buffer = remaining

          for (const event of events) {
            const actionType = `SSE_${event.type.toUpperCase()}` as ChatAction["type"]
            dispatch({ type: actionType, event } as ChatAction)
          }
        }

        dispatch({ type: "STREAM_END" })
        return true
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error"
        const desc = `POST /api/agents/${agentName}/${endpoint} threw (conversation: ${conversationId ?? "none"}): ${message}`
        toast.error("Failed to run agent", { description: desc })
        dispatch({ type: "STREAM_ERROR", error: desc })
        return false
      }
    },
    [agentName, backgroundMode, conversationId, onAuthError]
  )

  const sendMessage = useCallback(
    async (content: string, extraAttachmentIds?: string[]) => {
      if ((!content.trim() && attachments.length === 0 && !extraAttachmentIds?.length) || state.isLoading) return

      const trimmedContent = content.trim()
      dispatch({ type: "SEND_START", message: createMessage(trimmedContent, "user") })

      if (state.clarification) {
        dispatch({ type: "CLEAR_CLARIFICATION" })
      }

      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }

      // Capture and clear staged attachments, merge with any extra IDs (e.g. from pending messages)
      const attachmentIds = [...attachments.map((a) => a.id), ...(extraAttachmentIds ?? [])]
      setAttachments([])

      const shouldSendFlowContext =
        !flowContextSentRef.current && initialFlowContext && Object.keys(initialFlowContext).length > 0

      await streamRequest({
        message: trimmedContent,
        ...(shouldSendFlowContext && { flowContext: initialFlowContext }),
        ...(attachmentIds.length > 0 && { attachmentIds })
      })
    },
    [attachments, initialFlowContext, state.isLoading, state.clarification, streamRequest]
  )

  // Answer the active clarifyWithUser pause with structured per-question answers.
  // Echoes the pending tool-call id back so the resuming node (which may differ
  // from the pausing one) resolves the exact call against the durable state.
  const submitClarification = useCallback(
    async (answers: ClarificationAnswers | null) => {
      const pending = state.clarification
      if (!pending || state.isLoading) return

      const clarifyAnswers: ClarifyAnswer[] = pending.questions.map((_, i) => {
        const value = answers?.[i] ?? null
        return value === null ? { skipped: true } : { message: value }
      })

      // Show the answered turn as a ClarificationSummary card, not a fake text message.
      dispatch({
        type: "SEND_START",
        message: createMessage("", "user", {
          clarificationSummary: {
            questions: pending.questions.map((question) => question.message),
            answers: pending.questions.map((_, i) => answers?.[i] ?? null)
          }
        })
      })

      dispatch({ type: "CLEAR_CLARIFICATION" })

      const ok = await streamRequest({ clarifyToolCallId: pending.toolCallId, clarifyAnswers })

      if (!ok) dispatch({ type: "RESTORE_CLARIFICATION", clarification: pending })
    },
    [state.clarification, state.isLoading, streamRequest]
  )

  const dismissClarification = useCallback(() => {
    dispatch({ type: "CLEAR_CLARIFICATION" })
  }, [])

  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }, [])

  return {
    messages: state.messages,
    currentResponse: state.currentResponse,
    isLoading: state.isLoading,
    clarification: state.clarification,
    currentThought: state.currentThought,
    stepHistory: state.stepHistory,
    stepPath: state.stepPath,
    flowState: state.flowState,
    currentToolCall: state.currentToolCall,
    sendMessage,
    submitClarification,
    dismissClarification,
    cleanup,
    attachments,
    uploadFile,
    removeAttachment,
    isUploading
  }
}
